<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationApi from 'dashboard/api/inbox/conversation';
import AilabAssistantApi from 'dashboard/api/ailab/assistant';
import getWootAxios from 'dashboard/helper/getWootAxios';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  assistantId: {
    type: [Number, String],
    required: false,
    default: null,
  },
  lastCustomerMessage: {
    type: String,
    default: '',
  },
  pendingCustomerMessages: {
    type: Array,
    default: () => [],
  },
  isLastMessageCustomer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['insertReply', 'sendReply']);

const hasValidBot = computed(() => {
  return (
    props.assistantId !== null &&
    props.assistantId !== undefined &&
    Number(props.assistantId) > 0
  );
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const axios = getWootAxios();

const isLoading = ref(false);
const suggestion = ref(null);
const suggestionSegments = ref([]);
const suggestionId = ref(null);
// AI Studio media images (e.g. deposit QR) that will be attached as native
// images when this draft is sent — shown as a preview so the agent sees
// exactly what goes out, matching auto-reply behavior.
const suggestionImageUrls = ref([]);
const normalizeImageUrls = value =>
  Array.isArray(value)
    ? value.filter(u => typeof u === 'string' && u.trim() !== '')
    : [];
const errorMessage = ref('');
const isAutoLoaded = ref(false);
const sendingSeriesId = ref(null);
const isSendingSeries = ref(false);
const sendingStatus = ref(null);
const sendingSentCount = ref(null);
const sendingTotalCount = ref(null);
const sendingUpdatedAt = ref(null);
const replyMode = ref('suggest');
const botName = ref('');
const isSendingStep = ref(false);
const assistantInactive = ref(false);
const assistantInactiveReason = ref('');
const activeSuggestionStreamId = ref('');
let suggestionStreamStartTimeout = null;

const segmentSendMode = computed(
  () => uiSettings.value?.ai_assistant_segment_mode || 'sequence'
);
const isStepSendMode = computed(() => segmentSendMode.value === 'step');

const setSegmentSendMode = mode => {
  const next = mode === 'step' ? 'step' : 'sequence';
  updateUISettings({ ai_assistant_segment_mode: next });
};

const hasSuggestion = computed(() => !!suggestion.value);
const hasSegments = computed(
  () =>
    Array.isArray(suggestionSegments.value) &&
    suggestionSegments.value.length > 0
);

const isSendingAny = computed(() => {
  return (
    !!sendingSeriesId.value || isSendingSeries.value || isSendingStep.value
  );
});

const stepSentCountLocal = ref(0);
const stepTotalCount = computed(() => {
  if (hasSegments.value) return suggestionSegments.value.length;
  return typeof sendingTotalCount.value === 'number'
    ? sendingTotalCount.value
    : 0;
});
const stepSentCount = computed(() => {
  if (!isStepSendMode.value) return 0;
  const fromServer =
    typeof sendingSentCount.value === 'number' ? sendingSentCount.value : 0;
  return Math.max(stepSentCountLocal.value, fromServer);
});
const hasRemainingStepSegments = computed(() => {
  if (!isStepSendMode.value) return false;
  if (!hasSegments.value) return false;
  return stepSentCount.value < suggestionSegments.value.length;
});

const isSeriesTerminal = computed(() => {
  if (sendingStatus.value && sendingStatus.value !== 'sending') return true;
  const sent =
    typeof sendingSentCount.value === 'number'
      ? sendingSentCount.value
      : typeof sendingSentCount.value === 'string'
        ? Number(sendingSentCount.value)
        : null;
  const total =
    typeof sendingTotalCount.value === 'number'
      ? sendingTotalCount.value
      : typeof sendingTotalCount.value === 'string'
        ? Number(sendingTotalCount.value)
        : null;
  const effectiveTotal =
    typeof total === 'number' && Number.isFinite(total) && total > 0
      ? total
      : hasSegments.value
        ? suggestionSegments.value.length
        : null;
  if (
    typeof sent === 'number' &&
    Number.isFinite(sent) &&
    typeof effectiveTotal === 'number' &&
    effectiveTotal > 0 &&
    sent >= effectiveTotal
  ) {
    return true;
  }
  if (
    isStepSendMode.value &&
    hasSegments.value &&
    stepSentCount.value >= suggestionSegments.value.length
  ) {
    return true;
  }
  return false;
});

const showSendingControls = computed(() => {
  if (!sendingSeriesId.value) return false;
  return !isSeriesTerminal.value;
});

const aggregatedCustomerMessage = computed(() => {
  const messages = (props.pendingCustomerMessages || []).filter(
    msg => typeof msg === 'string' && msg.trim() !== ''
  );

  if (messages.length) {
    return messages.join('\n\n');
  }

  return props.lastCustomerMessage || '';
});

const clearSuggestionState = (preserveError = false) => {
  suggestion.value = null;
  suggestionSegments.value = [];
  suggestionImageUrls.value = [];
  suggestionId.value = null;
  isAutoLoaded.value = false;
  activeSuggestionStreamId.value = '';
  stepSentCountLocal.value = 0;
  if (!preserveError) {
    errorMessage.value = '';
  }
};

const markAssistantInactive = (reason = 'Assistant is inactive') => {
  assistantInactive.value = true;
  assistantInactiveReason.value = (
    reason || 'Assistant is inactive'
  ).toString();
  isLoading.value = false;
  clearSuggestionState(true);
  if (replyMode.value === 'auto_suggest') replyMode.value = 'suggest';
};

const closeSidebar = () => {
  updateUISettings({
    is_ai_assistant_open: false,
  });
};

const loadExistingSuggestion = async () => {
  if (!props.isLastMessageCustomer) {
    return;
  }
  try {
    const accountId = window.location.pathname.split('/')[3];
    const response = await axios.get(
      `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion`
    );

    if (response.data?.disabled) {
      markAssistantInactive(response.data?.error || 'Assistant is inactive');
      return;
    }

    sendingSeriesId.value = response.data?.sending_series_id || null;
    sendingStatus.value = response.data?.sending_status || null;
    sendingSentCount.value = response.data?.sending_sent_count ?? null;
    sendingTotalCount.value = response.data?.sending_total_count ?? null;
    sendingUpdatedAt.value = response.data?.sending_updated_at ?? null;
    if (
      segmentSendMode.value === 'step' &&
      typeof response.data?.sending_sent_count === 'number'
    ) {
      stepSentCountLocal.value = response.data.sending_sent_count;
    }

    if (sendingSeriesId.value && isSeriesTerminal.value) {
      sendingSeriesId.value = null;
    }

    const suggestionText = response.data?.suggestion_text;
    const segs = Array.isArray(response.data?.suggestion_segments)
      ? response.data.suggestion_segments.filter(
          s => typeof s === 'string' && s.trim() !== ''
        )
      : [];
    if (suggestionText) {
      suggestion.value = suggestionText;
      suggestionSegments.value = segs;
      suggestionImageUrls.value = normalizeImageUrls(response.data?.image_urls);
      suggestionId.value = response.data?.id;
      isAutoLoaded.value = true;
      replyMode.value = response.data.reply_mode || 'suggest';
    }
  } catch (error) {
    const apiError = error?.response?.data?.error || '';
    if (apiError && apiError.toString().toLowerCase().includes('inactive')) {
      markAssistantInactive(apiError);
      return;
    }
    console.error('[AI Assistant] Failed to load existing suggestion:', error);
  }
};

let sendingPollTimer = null;
const startSendingPoll = () => {
  if (sendingPollTimer) clearInterval(sendingPollTimer);
  sendingPollTimer = setInterval(async () => {
    if (!sendingSeriesId.value) return;
    await loadExistingSuggestion();
    if (isSeriesTerminal.value) {
      sendingSeriesId.value = null;
      clearInterval(sendingPollTimer);
      sendingPollTimer = null;
    }
  }, 1000);
};

onUnmounted(() => {
  if (sendingPollTimer) clearInterval(sendingPollTimer);
  sendingPollTimer = null;
  if (suggestionStreamStartTimeout) clearTimeout(suggestionStreamStartTimeout);
  suggestionStreamStartTimeout = null;
  emitter.off(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleSuggestionStreamEvent);
});

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });
const loadExistingSuggestionWithRetry = async (retries = 5, delay = 700) => {
  for (let i = 0; i < retries; i += 1) {
    await loadExistingSuggestion();
    if (suggestion.value) return;
    await wait(delay);
  }
};

const clearSuggestionStreamStartTimeout = () => {
  if (suggestionStreamStartTimeout) {
    clearTimeout(suggestionStreamStartTimeout);
    suggestionStreamStartTimeout = null;
  }
};

const finalizeSuggestionStream = data => {
  suggestion.value = data?.suggestion || '';
  suggestionSegments.value = Array.isArray(data?.suggestion_segments)
    ? data.suggestion_segments.filter(
        s => typeof s === 'string' && s.trim() !== ''
      )
    : [];
  suggestionImageUrls.value = normalizeImageUrls(data?.image_urls);
  suggestionId.value = data?.id || null;
  isAutoLoaded.value = false;
  activeSuggestionStreamId.value = '';
  isLoading.value = false;
  clearSuggestionStreamStartTimeout();
};

const failSuggestionStream = message => {
  activeSuggestionStreamId.value = '';
  isLoading.value = false;
  clearSuggestionStreamStartTimeout();
  errorMessage.value = message || 'Failed to generate suggestion';
};

const handleSuggestionStreamEvent = ({ event, data } = {}) => {
  if (event !== 'captain.suggestion.stream') return;
  if (!activeSuggestionStreamId.value) return;
  if (String(data?.stream_id || '') !== activeSuggestionStreamId.value) return;
  if (Number(data?.conversation_id) !== Number(props.conversationId)) return;

  switch (data?.state) {
    case 'started':
      break;
    case 'delta':
      clearSuggestionStreamStartTimeout();
      isLoading.value = false;
      suggestion.value = `${suggestion.value || ''}${data?.delta || ''}`;
      break;
    case 'completed':
      finalizeSuggestionStream(data);
      useAlert('New AI suggestion generated successfully!');
      break;
    case 'failed':
      failSuggestionStream(data?.error || 'Failed to generate suggestion');
      useAlert(data?.error || 'Failed to generate suggestion');
      break;
    default:
      break;
  }
};

const getSuggestionSync = async ({ accountId, isManualClick }) => {
  const messagePayload =
    aggregatedCustomerMessage.value || 'Generate a helpful response';
  const response = await axios.post(
    `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion`,
    {
      message: messagePayload,
      customer_messages: props.pendingCustomerMessages,
    }
  );

  if (response.data.success) {
    suggestion.value = response.data.suggestion;
    suggestionSegments.value = Array.isArray(response.data?.suggestion_segments)
      ? response.data.suggestion_segments.filter(
          s => typeof s === 'string' && s.trim() !== ''
        )
      : [];
    suggestionImageUrls.value = normalizeImageUrls(response.data?.image_urls);
    suggestionId.value = response.data.id;
    if (isManualClick) {
      useAlert('New AI suggestion generated successfully!');
    }
  }
};

const getSuggestionStream = async ({ accountId, isManualClick }) => {
  const messagePayload =
    aggregatedCustomerMessage.value || 'Generate a helpful response';
  const response = await axios.post(
    `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion`,
    {
      message: messagePayload,
      customer_messages: props.pendingCustomerMessages,
      stream: true,
    }
  );

  const streamId = String(response?.data?.stream_id || '').trim();
  if (response?.status !== 202 || !streamId) {
    throw new Error('Streaming transport was not accepted');
  }

  suggestion.value = '';
  suggestionSegments.value = [];
  suggestionImageUrls.value = [];
  suggestionId.value = null;
  activeSuggestionStreamId.value = streamId;
  clearSuggestionStreamStartTimeout();
  suggestionStreamStartTimeout = setTimeout(async () => {
    if (!activeSuggestionStreamId.value) return;
    activeSuggestionStreamId.value = '';
    suggestion.value = null;
    suggestionSegments.value = [];
    suggestionImageUrls.value = [];
    suggestionId.value = null;
    if (isManualClick) {
      useAlert(
        'Suggestion stream timed out, retrying with standard generation.'
      );
    }
    try {
      await getSuggestionSync({ accountId, isManualClick });
    } catch (syncError) {
      const apiError = syncError.response?.data?.error || '';
      errorMessage.value = apiError || 'Failed to generate suggestion';
      if (isManualClick) {
        useAlert(errorMessage.value);
      }
    } finally {
      isLoading.value = false;
      clearSuggestionStreamStartTimeout();
    }
  }, 5000);
};

const getSuggestion = async () => {
  if (isLoading.value) return;
  if (assistantInactive.value) return;

  if (!hasValidBot.value) {
    errorMessage.value = 'No AI bot configured for this inbox';
    useAlert('No AI bot configured for this inbox');
    return;
  }

  const isManualClick = !(
    replyMode.value === 'auto_suggest' &&
    props.isLastMessageCustomer &&
    (props.lastCustomerMessage || '').trim() !== ''
  );

  isLoading.value = true;
  errorMessage.value = '';
  suggestion.value = null;
  isAutoLoaded.value = false;

  try {
    const accountId = window.location.pathname.split('/')[3];
    await getSuggestionStream({ accountId, isManualClick });
  } catch (error) {
    console.error('Error generating suggestion:', error);
    const apiError = error.response?.data?.error || '';
    if (apiError && apiError.toString().toLowerCase().includes('inactive')) {
      markAssistantInactive(apiError);
      return;
    }
    try {
      const accountId = window.location.pathname.split('/')[3];
      await getSuggestionSync({ accountId, isManualClick });
    } catch (syncError) {
      const syncApiError = syncError.response?.data?.error || '';
      errorMessage.value =
        syncApiError || apiError || 'Failed to generate suggestion';
      if (isManualClick) {
        useAlert(errorMessage.value);
      }
    }
  } finally {
    if (!activeSuggestionStreamId.value) {
      isLoading.value = false;
    }
  }
};

const copySuggestion = () => {
  if (!suggestion.value) return;
  navigator.clipboard.writeText(suggestion.value).then(() => {
    useAlert('Copied to clipboard!');
  });
};

const insertToInput = () => {
  if (!suggestion.value) return;

  emit('insertReply', suggestion.value);
  useAlert('Inserted to input box');

  const accountId = window.location.pathname.split('/')[3];
  axios
    .post(
      `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/use`
    )
    .catch(err => {
      console.error('Failed to mark suggestion as used after insert:', err);
    });
  clearSuggestionState();
  if (replyMode.value !== 'auto_suggest') {
    replyMode.value = 'suggest';
  }
};

const sendNow = async () => {
  if (!suggestion.value) return;
  if (isSendingSeries.value) return;

  isSendingSeries.value = true;
  try {
    const accountId = window.location.pathname.split('/')[3];
    const url = `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/send`;
    const response = await axios.post(url);
    if (response.data?.success) {
      const seriesId = response.data?.series_id || null;
      sendingSeriesId.value = seriesId;
      if (seriesId) {
        sendingStatus.value = sendingStatus.value || 'sending';
        if (hasSegments.value) {
          sendingTotalCount.value = suggestionSegments.value.length;
          if (typeof sendingSentCount.value !== 'number') {
            sendingSentCount.value = 0;
          }
        }
      } else {
        clearSuggestionState();
      }
      if (replyMode.value !== 'auto_suggest') {
        replyMode.value = 'suggest';
      }
      useAlert(
        sendingSeriesId.value
          ? 'Sending segmented reply…'
          : 'Reply sent successfully!'
      );
    } else {
      useAlert(response.data?.error || 'Failed to send suggestion');
    }
  } catch (error) {
    console.error('Error sending suggestion (ailab):', error);
    useAlert(error.response?.data?.error || 'Failed to send suggestion');
  } finally {
    isSendingSeries.value = false;
  }
};

const sendNextSegment = async () => {
  if (!hasSegments.value) {
    useAlert('No segments available');
    return;
  }
  if (!hasRemainingStepSegments.value) {
    useAlert('All segments have been sent');
    return;
  }
  const accountId = window.location.pathname.split('/')[3];
  const url = `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/send_next`;
  try {
    if (isSendingStep.value) return;
    isSendingStep.value = true;
    const response = await axios.post(url);
    if (response.data?.success) {
      sendingSeriesId.value = response.data?.series_id || sendingSeriesId.value;
      stepSentCountLocal.value = Math.min(
        stepSentCountLocal.value + 1,
        suggestionSegments.value.length
      );
      useAlert('Sent next segment');
      await loadExistingSuggestion();
    } else {
      useAlert(response.data?.error || 'Failed to send next segment');
    }
  } catch (error) {
    console.error('Error sending next segment (ailab):', error);
    useAlert(error.response?.data?.error || 'Failed to send next segment');
  } finally {
    isSendingStep.value = false;
  }
};

const resetStepSend = async () => {
  const accountId = window.location.pathname.split('/')[3];
  const url = `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/step_reset`;
  try {
    await axios.post(url);
    sendingSeriesId.value = null;
    sendingStatus.value = 'cancelled';
    stepSentCountLocal.value = 0;
    useAlert('Step-send reset');
    await loadExistingSuggestion();
  } catch (error) {
    console.error('Failed to reset step-send:', error);
    useAlert('Failed to reset step-send');
  }
};

const cancelSending = async () => {
  const accountId = window.location.pathname.split('/')[3];
  const url = `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/cancel`;
  try {
    await axios.post(url);
    sendingSeriesId.value = null;
    sendingStatus.value = 'cancelled';
    useAlert('Cancelled remaining segments');
  } catch (error) {
    console.error('Failed to cancel sending:', error);
    useAlert('Failed to cancel sending');
  }
};

const dismissSuggestion = () => {
  const accountId = window.location.pathname.split('/')[3];
  axios
    .post(
      `/api/v1/accounts/${accountId}/ailab/conversations/${props.conversationId}/suggestion/use`
    )
    .catch(err => {
      console.error('Failed to mark suggestion as dismissed:', err);
    });
  clearSuggestionState();
  if (replyMode.value !== 'auto_suggest') {
    replyMode.value = 'suggest';
  }
};

watch(
  () => props.conversationId,
  () => {
    clearSuggestionState();
    sendingSeriesId.value = null;
    sendingStatus.value = null;
    sendingSentCount.value = null;
    sendingTotalCount.value = null;
    sendingUpdatedAt.value = null;
    replyMode.value = replyMode.value || 'auto_reply';
    hasSyncedPendingMessages = false;
    loadExistingSuggestion();
  }
);

watch(
  () => sendingSeriesId.value,
  sid => {
    if (sid) startSendingPoll();
  }
);

const fetchBotName = async () => {
  if (!hasValidBot.value) {
    botName.value = '';
    return;
  }

  try {
    const response = await ConversationApi.getInboxAssistant(
      props.conversationId
    );
    const assistant = response.data?.assistant;
    botName.value = assistant?.name || 'AILAB';
    replyMode.value = assistant?.wlab_ai_reply_mode || 'auto_reply';
  } catch (error) {
    console.error('[AI Assistant] Failed to fetch bot name:', error);
    botName.value = '';
  }
};

onMounted(() => {
  loadExistingSuggestion();
  fetchBotName();
  emitter.on(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleSuggestionStreamEvent);
});

watch(
  () => ({
    isCustomer: props.isLastMessageCustomer,
    hasSuggestion: hasSuggestion.value,
    loading: isLoading.value,
    mode: replyMode.value,
    message: props.lastCustomerMessage,
  }),
  state => {
    if (
      state.isCustomer &&
      !state.hasSuggestion &&
      !state.loading &&
      !activeSuggestionStreamId.value &&
      !assistantInactive.value &&
      !isSendingAny.value &&
      state.mode === 'auto_suggest' &&
      (state.message || '').trim() !== ''
    ) {
      getSuggestion();
    }
  },
  { deep: true }
);

let hasSyncedPendingMessages = false;

watch(
  () => props.pendingCustomerMessages,
  async (newMessages, oldMessages) => {
    const currentMessages = Array.isArray(newMessages) ? newMessages : [];
    const previousMessages = Array.isArray(oldMessages) ? oldMessages : [];

    if (!hasSyncedPendingMessages) {
      hasSyncedPendingMessages = true;
      return;
    }

    const newCount = currentMessages.length;
    const oldCount = previousMessages.length;

    if (!props.isLastMessageCustomer || newCount === 0) {
      if (oldCount !== newCount) {
        clearSuggestionState();
      }
      return;
    }

    const messagesChanged =
      newCount !== oldCount ||
      JSON.stringify(currentMessages) !== JSON.stringify(previousMessages);

    if (messagesChanged) {
      clearSuggestionState();

      if (replyMode.value === 'auto_suggest') {
        if (assistantInactive.value) return;
        if (isSendingAny.value) return;
        await loadExistingSuggestionWithRetry(5, 700);

        if (
          !suggestion.value &&
          !isLoading.value &&
          !activeSuggestionStreamId.value
        ) {
          await getSuggestion();
        }
      }
    }
  },
  { deep: true }
);

watch(
  () => props.assistantId,
  () => {
    fetchBotName();
  }
);

const updateAilabReplyMode = async newMode => {
  if (!props.assistantId) return;
  try {
    await AilabAssistantApi.update(props.assistantId, {
      assistant: {
        config: {
          wlab_ai_reply_mode: newMode,
        },
      },
    });
  } catch (error) {
    console.error('[AI Assistant] Failed to update ailab reply mode:', error);
  }
};
</script>

<template>
  <div class="aia-root">
    <!-- Header -->
    <div class="aia-header">
      <h2 class="aia-header-title">AI Assistant</h2>
      <div class="aia-header-actions">
        <button v-if="botName" class="wlabai-bot-badge">
          <svg
            viewBox="0 0 24 24"
            height="12"
            width="12"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="none">
              <path
                d="M9.107 5.448c.598-1.75 3.016-1.803 3.725-.159l.06.16l.807 2.36a4 4 0 0 0 2.276 2.411l.217.081l2.36.806c1.75.598 1.803 3.016.16 3.725l-.16.06l-2.36.807a4 4 0 0 0-2.412 2.276l-.081.216l-.806 2.361c-.598 1.75-3.016 1.803-3.724.16l-.062-.16l-.806-2.36a4 4 0 0 0-2.276-2.412l-.216-.081l-2.36-.806c-1.751-.598-1.804-3.016-.16-3.724l.16-.062l2.36-.806A4 4 0 0 0 8.22 8.025l.081-.216z"
                fill="currentColor"
              />
            </g>
          </svg>
          <span>{{ botName }}</span>
        </button>
        <button class="aia-close-btn" @click="closeSidebar">
          <span class="i-lucide-x size-[18px]" />
        </button>
      </div>
    </div>

    <!-- Scrollable content area -->
    <div class="aia-content">
      <!-- ====== EMPTY STATE ====== -->
      <div
        v-if="!hasSuggestion && !isLoading && !errorMessage"
        class="aia-empty"
      >
        <div class="aia-empty-orb-wrap">
          <div class="loader aia-orb-empty">
            <svg width="100" height="100" viewBox="0 0 100 100">
              <defs>
                <mask id="clipping">
                  <polygon points="0,0 100,0 100,100 0,100" fill="black" />
                  <polygon points="25,25 75,25 50,75" fill="white" />
                  <polygon points="50,25 75,75 25,75" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                </mask>
              </defs>
            </svg>
            <div class="box" />
          </div>
        </div>

        <h3 class="aia-empty-title">AI Suggestions</h3>
        <p class="aia-empty-desc">
          Generate smart, context-aware responses powered by AI
        </p>

        <button
          :disabled="!hasValidBot"
          class="aia-generate-btn"
          @click="getSuggestion"
        >
          <span class="i-lucide-sparkles size-4" />
          <span>{{
            hasValidBot ? 'Generate Suggestion' : 'No Bot Configured'
          }}</span>
        </button>

        <p class="aia-powered">
          <span class="aia-powered-dot" />
          Powered by WlabAI
          <span class="aia-powered-dot" />
        </p>
      </div>

      <!-- ====== LOADING STATE ====== -->
      <div v-else-if="isLoading" class="aia-loading">
        <div class="loader aia-orb-loading">
          <svg width="100" height="100" viewBox="0 0 100 100">
            <defs>
              <mask id="clipping">
                <polygon points="0,0 100,0 100,100 0,100" fill="black" />
                <polygon points="25,25 75,25 50,75" fill="white" />
                <polygon points="50,25 75,75 25,75" fill="white" />
                <polygon points="35,35 65,35 50,65" fill="white" />
                <polygon points="35,35 65,35 50,65" fill="white" />
                <polygon points="35,35 65,35 50,65" fill="white" />
                <polygon points="35,35 65,35 50,65" fill="white" />
              </mask>
            </defs>
          </svg>
          <div class="box" />
        </div>

        <p class="aia-loading-title">
          WLAB AI is thinking
          <span class="aia-loading-dots">
            <span style="animation-delay: 0ms">.</span>
            <span style="animation-delay: 150ms">.</span>
            <span style="animation-delay: 300ms">.</span>
          </span>
        </p>
        <p class="aia-loading-sub">This may take a few seconds</p>
      </div>

      <!-- ====== SUGGESTION STATE ====== -->
      <div v-else-if="hasSuggestion" class="aia-suggestion">
        <!-- Suggestion header -->
        <div class="aia-suggestion-header">
          <div class="aia-suggestion-meta">
            <div class="aia-mini-orb-wrap">
              <div class="loader aia-orb-mini">
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <defs>
                    <mask id="clipping">
                      <polygon points="0,0 100,0 100,100 0,100" fill="black" />
                      <polygon points="25,25 75,25 50,75" fill="white" />
                      <polygon points="50,25 75,75 25,75" fill="white" />
                      <polygon points="35,35 65,35 50,65" fill="white" />
                      <polygon points="35,35 65,35 50,65" fill="white" />
                      <polygon points="35,35 65,35 50,65" fill="white" />
                      <polygon points="35,35 65,35 50,65" fill="white" />
                    </mask>
                  </defs>
                </svg>
                <div class="box" />
              </div>
            </div>
            <div>
              <div class="aia-suggestion-label">AI Suggestion</div>
              <div
                v-if="isAutoLoaded"
                class="aia-suggestion-badge aia-badge-auto"
              >
                <span class="i-lucide-check-circle size-3" />
                Auto-generated
              </div>
              <div v-else class="aia-suggestion-badge aia-badge-fresh">
                <span class="i-lucide-sparkles size-3" />
                Freshly created
              </div>
            </div>
          </div>
          <button
            :disabled="!hasValidBot"
            class="aia-regen-btn"
            @click="getSuggestion"
          >
            <span class="i-lucide-refresh-cw size-[14px]" />
            Regenerate
          </button>
        </div>

        <!-- Suggestion body -->
        <div class="aia-suggestion-body">
          <div class="aia-suggestion-scroll">
            <template v-if="hasSegments">
              <div class="aia-segments">
                <div
                  v-for="(seg, idx) in suggestionSegments"
                  :key="`${idx}-${seg.slice(0, 20)}`"
                  class="aia-segment-card"
                  :class="{ 'is-sent': idx < stepSentCount }"
                >
                  <div class="aia-segment-head">
                    <span class="aia-segment-num">Segment {{ idx + 1 }}</span>
                    <button
                      class="aia-segment-copy"
                      @click="
                        navigator.clipboard.writeText(seg);
                        useAlert('Segment copied');
                      "
                    >
                      Copy
                    </button>
                  </div>
                  <div class="aia-segment-text">{{ seg }}</div>
                </div>
              </div>
            </template>
            <div v-else class="aia-suggestion-text">
              {{ suggestion }}
            </div>

            <!-- Media image preview: shows the image (e.g. deposit QR) that
                 will be attached when this draft is sent, matching auto-reply. -->
            <div v-if="suggestionImageUrls.length" class="aia-image-preview">
              <div class="aia-image-preview-label">
                <span class="i-lucide-image size-[13px]" />
                {{
                  suggestionImageUrls.length > 1
                    ? `${suggestionImageUrls.length} images will be attached`
                    : 'Image will be attached'
                }}
              </div>
              <div class="aia-image-preview-thumbs">
                <img
                  v-for="(imgUrl, imgIdx) in suggestionImageUrls"
                  :key="imgIdx"
                  :src="imgUrl"
                  class="aia-image-preview-thumb"
                  alt="Attachment preview"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons area -->
        <div class="aia-actions">
          <!-- Sending progress -->
          <div v-if="showSendingControls" class="aia-sending-progress">
            <div class="aia-sending-row">
              <span class="aia-sending-label">
                {{
                  sendingStatus === 'completed'
                    ? 'Completed'
                    : sendingStatus === 'cancelled'
                      ? 'Cancelled'
                      : sendingStatus === 'interrupted'
                        ? 'Stopped (customer replied)'
                        : 'Sending…'
                }}
              </span>
              <span
                v-if="sendingSentCount !== null && sendingTotalCount !== null"
                class="aia-sending-count"
              >
                {{ sendingSentCount }}/{{ sendingTotalCount }}
              </span>
            </div>
          </div>

          <!-- Non-segmented send -->
          <button
            v-if="provider !== 'ailab' || !hasSegments"
            :disabled="
              provider === 'ailab' && (isSendingSeries || !!sendingSeriesId)
            "
            class="aia-primary-btn"
            @click="sendNow"
          >
            <span class="i-lucide-send size-4" />
            {{
              provider === 'ailab' && isSendingSeries
                ? 'Sending…'
                : 'Send Reply Now'
            }}
          </button>

          <!-- Segmented actions -->
          <div v-else class="aia-seg-actions">
            <div class="aia-seg-mode-row">
              <span class="aia-seg-mode-label">Segment mode</span>
              <div class="aia-seg-mode-pills">
                <button
                  class="aia-seg-pill"
                  :class="{ 'is-active': segmentSendMode === 'sequence' }"
                  @click="setSegmentSendMode('sequence')"
                >
                  Sequence
                </button>
                <button
                  class="aia-seg-pill"
                  :class="{ 'is-active': segmentSendMode === 'step' }"
                  @click="setSegmentSendMode('step')"
                >
                  Step
                </button>
              </div>
            </div>

            <button
              :disabled="
                provider === 'ailab' &&
                (isSendingSeries ||
                  isSendingStep ||
                  (!isStepSendMode && !!sendingSeriesId))
              "
              class="aia-primary-btn"
              @click="isStepSendMode ? sendNextSegment() : sendNow()"
            >
              <span
                :class="
                  isStepSendMode
                    ? 'i-lucide-forward size-4'
                    : 'i-lucide-send size-4'
                "
              />
              {{
                provider === 'ailab' && (isSendingSeries || isSendingStep)
                  ? 'Sending…'
                  : isStepSendMode
                    ? hasRemainingStepSegments
                      ? `Send next (${Math.min(stepSentCount + 1, stepTotalCount)}/${stepTotalCount || suggestionSegments.length})`
                      : 'All sent'
                    : 'Send all (paced)'
              }}
            </button>

            <button
              v-if="isStepSendMode"
              class="aia-secondary-btn"
              @click="resetStepSend"
            >
              <span class="i-lucide-rotate-ccw size-[14px]" />
              Reset step-send
            </button>
          </div>

          <!-- Cancel sending -->
          <button
            v-if="showSendingControls"
            class="aia-secondary-btn aia-cancel-btn"
            @click="cancelSending"
          >
            <span class="i-lucide-x size-[14px]" />
            Cancel remaining segments
          </button>

          <!-- Bottom action row -->
          <div class="aia-bottom-actions">
            <button class="aia-action-chip" @click="insertToInput">
              <span class="i-lucide-pen-line size-[14px]" />
              Edit
            </button>
            <button class="aia-action-chip" @click="copySuggestion">
              <span class="i-lucide-copy size-[14px]" />
              Copy
            </button>
            <button
              class="aia-action-chip aia-chip-dismiss"
              @click="dismissSuggestion"
            >
              <span class="i-lucide-x size-[14px]" />
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <!-- ====== ERROR STATE ====== -->
      <div v-else-if="errorMessage" class="aia-error">
        <div class="aia-error-icon-wrap">
          <span class="i-lucide-triangle-alert size-8" />
        </div>
        <h4 class="aia-error-title">Something went wrong</h4>
        <p class="aia-error-desc">{{ errorMessage }}</p>
        <button
          :disabled="!hasValidBot"
          class="aia-retry-btn"
          @click="getSuggestion"
        >
          <span class="i-lucide-refresh-cw size-4" />
          {{ hasValidBot ? 'Try Again' : 'No Bot Configured' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== DESIGN TOKENS ===== */
.aia-root {
  --aia-bg: #ffffff;
  --aia-bg-subtle: #f8fafc;
  --aia-bg-card: #f1f5f9;
  --aia-bg-segment: #ffffff;
  --aia-border: #e2e8f0;
  --aia-border-subtle: rgba(15, 23, 42, 0.06);
  --aia-text: #0f172a;
  --aia-text-secondary: #64748b;
  --aia-text-muted: #94a3b8;
  --aia-accent: #3b82f6;
  --aia-accent-hover: #2563eb;
  --aia-chip-bg: #f1f5f9;
  --aia-chip-hover: #e2e8f0;
  --aia-chip-text: #334155;
  --aia-dismiss-text: #ef4444;
  --aia-dismiss-hover: #fef2f2;
  --aia-cancel-text: #d97706;
  --aia-success: #10b981;
  --aia-primary-bg: #0f172a;
  --aia-primary-text: #ffffff;
  --aia-primary-hover: #1e293b;
  --aia-badge-bg: rgba(16, 185, 129, 0.08);
  --aia-badge-text: #059669;
  --aia-error-bg: #fef2f2;
  --aia-error-text: #dc2626;
  --aia-seg-pill-bg: #f1f5f9;
  --aia-seg-pill-active: #0f172a;
  --aia-seg-pill-active-text: #ffffff;
  --aia-progress-bg: #f8fafc;

  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: var(--aia-bg);
  color: var(--aia-text);
  font-size: 13px;
  line-height: 1.5;
}

/* ===== HEADER ===== */
.aia-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  max-height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid var(--aia-border);
  flex-shrink: 0;
}

.aia-header-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--aia-text);
  margin: 0;
}

.aia-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Restore original bot badge design */
.wlabai-bot-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  font-size: 0.65rem;
  cursor: pointer;
  position: relative;
  background: linear-gradient(90deg, #5bfcc4, #f593e4, #71a4f0);
  border-radius: 8px;
  color: #fff;
  transition: all 0.3s ease;
  box-shadow:
    inset 0px 0px 5px rgba(255, 255, 255, 0.66),
    inset 0px 35px 30px #000,
    0px 5px 10px rgba(0, 0, 0, 0.8);
  text-shadow: 1px 1px 1px #000;
  font-weight: 600;
}

.wlabai-bot-badge::before {
  content: '';
  position: absolute;
  inset: 0;
  margin: auto;
  border-radius: 10px;
  filter: blur(0);
  z-index: -1;
  box-shadow: none;
  background: conic-gradient(
    transparent 80deg,
    #40baf7,
    #f34ad7,
    #5bfcc4,
    transparent 280deg
  );
  transition: all 0.3s ease;
}

.wlabai-bot-badge:hover::before {
  filter: blur(15px);
}

.wlabai-bot-badge:active::before {
  filter: blur(5px);
  transform: translateY(1px);
}

.wlabai-bot-badge:active {
  box-shadow:
    inset 0px 0px 5px rgba(255, 255, 255, 0.66),
    inset 0px 35px 30px #000;
  margin-top: 3px;
}

.aia-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--aia-text-secondary);
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
}
.aia-close-btn:hover {
  background: var(--aia-chip-bg);
  color: var(--aia-text);
}

/* ===== CONTENT AREA ===== */
.aia-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

/* ===== EMPTY STATE ===== */
.aia-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  animation: aia-fadein 0.25s ease-out;
}

.aia-empty-orb-wrap {
  margin-bottom: 28px;
}

.aia-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--aia-text);
  margin: 0 0 8px;
}

.aia-empty-desc {
  font-size: 13px;
  color: var(--aia-text-muted);
  margin: 0 0 28px;
  max-width: 260px;
  line-height: 1.6;
}

.aia-generate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: var(--aia-primary-bg);
  color: var(--aia-primary-text);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.15);
  transition:
    background-color 0.15s,
    box-shadow 0.15s,
    transform 0.1s;
}
.aia-generate-btn:hover {
  background: var(--aia-primary-hover);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.2);
  transform: translateY(-1px);
}
.aia-generate-btn:active {
  transform: translateY(0);
}
.aia-generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.aia-powered {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 24px;
  font-size: 11px;
  color: var(--aia-text-muted);
}
.aia-powered-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--aia-text-muted);
  opacity: 0.5;
}

/* ===== LOADING STATE ===== */
.aia-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  animation: aia-fadein 0.25s ease-out;
}

.aia-loading-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--aia-accent);
  margin: 20px 0 6px;
}

.aia-loading-dots span {
  display: inline-block;
  animation: aia-bounce 1s infinite;
  font-weight: 800;
}

.aia-loading-sub {
  font-size: 12px;
  color: var(--aia-text-muted);
  margin: 0;
}

/* ===== SUGGESTION STATE ===== */
.aia-suggestion {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  animation: aia-fadein 0.25s ease-out;
  overflow: hidden;
}

.aia-suggestion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.aia-suggestion-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.aia-mini-orb-wrap {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.aia-suggestion-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--aia-text);
  line-height: 1.2;
}

.aia-suggestion-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-top: 2px;
}
.aia-badge-auto {
  color: var(--aia-badge-text);
}
.aia-badge-fresh {
  color: var(--aia-accent);
}

.aia-regen-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  color: var(--aia-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}
.aia-regen-btn:hover {
  background: var(--aia-chip-bg);
  border-color: var(--aia-border);
  color: var(--aia-text);
}
.aia-regen-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Suggestion body */
.aia-suggestion-body {
  flex: 1;
  overflow: hidden;
  border-radius: 14px;
  background: var(--aia-bg-card);
  margin-bottom: 16px;
  min-height: 0;
}

.aia-suggestion-scroll {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.aia-segments {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.aia-segment-card {
  padding: 14px;
  border-radius: 12px;
  background: var(--aia-bg-segment);
  border: 1px solid var(--aia-border-subtle);
  transition: opacity 0.2s;
}
.aia-segment-card.is-sent {
  opacity: 0.45;
}

.aia-segment-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.aia-segment-num {
  font-size: 12px;
  font-weight: 600;
  color: var(--aia-text-secondary);
}

.aia-segment-copy {
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 500;
  color: var(--aia-accent);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.15s;
}
.aia-segment-copy:hover {
  background: rgba(59, 130, 246, 0.08);
}

.aia-segment-text {
  font-size: 13px;
  line-height: 1.65;
  color: var(--aia-text);
  white-space: pre-wrap;
  word-break: break-word;
}

.aia-suggestion-text {
  font-size: 13px;
  line-height: 1.65;
  color: var(--aia-text);
  white-space: pre-wrap;
  word-break: break-word;
}

/* ===== MEDIA IMAGE PREVIEW ===== */
.aia-image-preview {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--aia-border, #e5e7eb);
}

.aia-image-preview-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--aia-text-muted, #6b7280);
  margin-bottom: 6px;
}

.aia-image-preview-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.aia-image-preview-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--aia-border, #e5e7eb);
  background: #f9fafb;
}

/* ===== ACTIONS ===== */
.aia-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.aia-primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: var(--aia-primary-bg);
  color: var(--aia-primary-text);
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.12);
  transition:
    background-color 0.15s,
    box-shadow 0.15s,
    transform 0.1s;
}
.aia-primary-btn:hover {
  background: var(--aia-primary-hover);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.18);
  transform: translateY(-1px);
}
.aia-primary-btn:active {
  transform: translateY(0);
}
.aia-primary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.aia-secondary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  border: 1px solid var(--aia-border);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  color: var(--aia-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}
.aia-secondary-btn:hover {
  background: var(--aia-chip-bg);
  color: var(--aia-text);
}

.aia-cancel-btn {
  color: var(--aia-cancel-text);
  border-color: rgba(217, 119, 6, 0.2);
}
.aia-cancel-btn:hover {
  background: rgba(217, 119, 6, 0.06);
  color: var(--aia-cancel-text);
}

/* Segment mode */
.aia-seg-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.aia-seg-mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--aia-chip-bg);
}

.aia-seg-mode-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--aia-text-secondary);
}

.aia-seg-mode-pills {
  display: flex;
  gap: 2px;
  padding: 2px;
  border-radius: 8px;
  background: var(--aia-bg);
}

.aia-seg-pill {
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--aia-text-secondary);
  transition: all 0.15s;
}
.aia-seg-pill.is-active {
  background: var(--aia-seg-pill-active);
  color: var(--aia-seg-pill-active-text);
}

/* Bottom action chips */
.aia-bottom-actions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.aia-action-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 8px;
  border: 1px solid var(--aia-border);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  color: var(--aia-chip-text);
  cursor: pointer;
  transition: all 0.15s;
}
.aia-action-chip:hover {
  background: var(--aia-chip-hover);
}

.aia-chip-dismiss {
  color: var(--aia-dismiss-text);
  border-color: rgba(239, 68, 68, 0.15);
}
.aia-chip-dismiss:hover {
  background: var(--aia-dismiss-hover);
}

/* Sending progress */
.aia-sending-progress {
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--aia-progress-bg);
  border: 1px solid var(--aia-border);
}
.aia-sending-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.aia-sending-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--aia-text);
}
.aia-sending-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--aia-text-secondary);
}

/* ===== ERROR STATE ===== */
.aia-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  animation: aia-fadein 0.25s ease-out;
}

.aia-error-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: var(--aia-error-bg);
  color: var(--aia-error-text);
}

.aia-error-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--aia-text);
  margin: 0 0 6px;
}

.aia-error-desc {
  font-size: 13px;
  color: var(--aia-text-muted);
  margin: 0 0 20px;
  max-width: 260px;
}

.aia-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: var(--aia-error-text);
  color: #fff;
  transition: opacity 0.15s;
}
.aia-retry-btn:hover {
  opacity: 0.9;
}
.aia-retry-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== ORB LOADER (preserved from original) ===== */
.loader {
  --color-one: #ffbf48;
  --color-two: #be4a1d;
  --color-three: #ffbf4780;
  --color-four: #bf4a1d80;
  --color-five: #ffbf4740;
  --time-animation: 2s;
  --size: 1;
  position: relative;
  border-radius: 50%;
  transform: scale(var(--size));
  box-shadow:
    0 0 25px 0 var(--color-three),
    0 20px 50px 0 var(--color-four);
  animation: colorize calc(var(--time-animation) * 3) ease-in-out infinite;
}
.loader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border-top: solid 1px var(--color-one);
  border-bottom: solid 1px var(--color-two);
  background: linear-gradient(180deg, var(--color-five), var(--color-four));
  box-shadow:
    inset 0 10px 10px 0 var(--color-three),
    inset 0 -10px 10px 0 var(--color-four);
}
.loader .box {
  width: 100px;
  height: 100px;
  background: linear-gradient(
    180deg,
    var(--color-one) 30%,
    var(--color-two) 70%
  );
  mask: url(#clipping);
  -webkit-mask: url(#clipping);
}
.loader svg {
  position: absolute;
}
.loader svg #clipping {
  filter: contrast(15);
  animation: roundness calc(var(--time-animation) / 2) linear infinite;
}
.loader svg #clipping polygon {
  filter: blur(7px);
}
.loader svg #clipping polygon:nth-child(1) {
  transform-origin: 75% 25%;
  transform: rotate(90deg);
}
.loader svg #clipping polygon:nth-child(2) {
  transform-origin: 50% 50%;
  animation: rotation var(--time-animation) linear infinite reverse;
}
.loader svg #clipping polygon:nth-child(3) {
  transform-origin: 50% 60%;
  animation: rotation var(--time-animation) linear infinite;
  animation-delay: calc(var(--time-animation) / -3);
}
.loader svg #clipping polygon:nth-child(4) {
  transform-origin: 40% 40%;
  animation: rotation var(--time-animation) linear infinite reverse;
}
.loader svg #clipping polygon:nth-child(5) {
  transform-origin: 40% 40%;
  animation: rotation var(--time-animation) linear infinite reverse;
  animation-delay: calc(var(--time-animation) / -2);
}
.loader svg #clipping polygon:nth-child(6) {
  transform-origin: 60% 40%;
  animation: rotation var(--time-animation) linear infinite;
}
.loader svg #clipping polygon:nth-child(7) {
  transform-origin: 60% 40%;
  animation: rotation var(--time-animation) linear infinite;
  animation-delay: calc(var(--time-animation) / -1.5);
}

.aia-orb-empty {
  --size: 0.85;
}
.aia-orb-loading {
  --size: 1;
}
.aia-orb-mini {
  --size: 0.24 !important;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes roundness {
  0% {
    filter: contrast(15);
  }
  20% {
    filter: contrast(3);
  }
  40% {
    filter: contrast(3);
  }
  60% {
    filter: contrast(15);
  }
  100% {
    filter: contrast(15);
  }
}
@keyframes colorize {
  0% {
    filter: hue-rotate(0deg);
  }
  20% {
    filter: hue-rotate(-30deg);
  }
  40% {
    filter: hue-rotate(-60deg);
  }
  60% {
    filter: hue-rotate(-90deg);
  }
  80% {
    filter: hue-rotate(-45deg);
  }
  100% {
    filter: hue-rotate(0deg);
  }
}
@keyframes aia-fadein {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes aia-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
}
</style>

<!-- Non-scoped: dark theme overrides -->
<style>
/* Dark theme tokens (pure dark only; mix should stay light) */
html.dark body:not(.theme-mix) .aia-root,
body.dark:not(.theme-mix) .aia-root {
  --aia-bg: #202024;
  --aia-bg-subtle: #161921;
  --aia-bg-card: #1a1d27;
  --aia-bg-segment: #21242e;
  --aia-border: rgba(255, 255, 255, 0.08);
  --aia-border-subtle: rgba(255, 255, 255, 0.05);
  --aia-text: #f1f5f9;
  --aia-text-secondary: #94a3b8;
  --aia-text-muted: #64748b;
  --aia-accent: #60a5fa;
  --aia-accent-hover: #3b82f6;
  --aia-chip-bg: rgba(255, 255, 255, 0.06);
  --aia-chip-hover: rgba(255, 255, 255, 0.1);
  --aia-chip-text: #cbd5e1;
  --aia-dismiss-text: #f87171;
  --aia-dismiss-hover: rgba(239, 68, 68, 0.08);
  --aia-cancel-text: #fbbf24;
  --aia-success: #34d399;
  --aia-primary-bg: #f1f5f9;
  --aia-primary-text: #0f172a;
  --aia-primary-hover: #e2e8f0;
  --aia-badge-bg: rgba(52, 211, 153, 0.1);
  --aia-badge-text: #34d399;
  --aia-error-bg: rgba(239, 68, 68, 0.08);
  --aia-error-text: #f87171;
  --aia-seg-pill-bg: rgba(255, 255, 255, 0.06);
  --aia-seg-pill-active: #f1f5f9;
  --aia-seg-pill-active-text: #0f172a;
  --aia-progress-bg: rgba(255, 255, 255, 0.04);
}

/* Light + Mix themes: use light palette (default vars apply, no override needed) */
/* body.theme-mix uses same light tokens as default */
</style>
